import { appConfirm } from "@/lib/appDialog";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { EmptyState, ErrorState } from "@/components/StateViews";
import { toast } from "sonner";
import { Video, Plus, Loader2, Calendar, Users, ExternalLink, X, Save, Trash2, FileText } from "lucide-react";

export default function MentorSessions() {
  const [searchParams] = useSearchParams();
  const focusedSession = searchParams.get("session");
  const requestedStudent = searchParams.get("student") || "";
  const requestedApplication = searchParams.get("application") || "";
  const [handledDeepLink, setHandledDeepLink] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ student_id: "", application_id: "", title: "", scheduled_at: "", duration_minutes: 45, agenda: "", repeat_weekly: false, repeat_weeks: 4, session_type: "individual" });
  const [saving, setSaving] = useState(false);
  const [notesFor, setNotesFor] = useState(null);
  const [notes, setNotes] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/sessions");
      setSessions(data.sessions || []);
      const { data: options } = await api.get("/sessions/options");
      setStudents((options.items || []).map((item) => ({ id: item.student_id, application_id: item.application_id, name: item.student_name, association_id: item.association_id, association_name: item.association_name })));
    } catch (e) {
      setError(formatApiError(e));
      toast.error(formatApiError(e));
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (loading || handledDeepLink) return;
    if (searchParams.get("new") === "1") { setForm((current) => ({ ...current, student_id: requestedStudent, application_id: requestedApplication })); setShowForm(true); setHandledDeepLink(true); return; }
    if (focusedSession) {
      const item = sessions.find((session) => session.id === focusedSession);
      document.getElementById(`mentor-session-${focusedSession}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      if (item && searchParams.get("notes") === "1") { setNotesFor(item); setNotes(item.notes || ""); }
      if (item) setHandledDeepLink(true);
    }
  }, [loading, sessions, focusedSession, requestedStudent, requestedApplication, searchParams, handledDeepLink]);

  const selectedStudent = students.find((student) => student.application_id === form.application_id);

  const create = async () => {
    if (!form.student_id || !form.title || !form.scheduled_at) { toast.error("جميع الحقول مطلوبة"); return; }
    if (form.session_type === "tripartite" && !selectedStudent?.association_id) { toast.error("هذا الطالب غير مرتبط بجمعية نشطة، لذلك لا يمكن إنشاء جلسة ثلاثية"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/sessions", { ...form, duration_minutes: parseInt(form.duration_minutes) || 45 });
      if (data.created_count > 1) toast.success("تمت جدولة " + data.created_count + " جلسات أسبوعية");
      else if (data.session?.meeting_status === "ready") toast.success("تم جدولة الجلسة وإنشاء رابط Teams");
      else toast.warning("حُفظت الجلسة، لكن تعذر إنشاء رابط Teams. يمكنك إعادة المحاولة من بطاقة الجلسة.");
      setShowForm(false);
      setForm({ student_id: "", application_id: "", title: "", scheduled_at: "", duration_minutes: 45, agenda: "", repeat_weekly: false, repeat_weeks: 4, session_type: "individual" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const saveNotes = async () => {
    try {
      await api.patch(`/sessions/${notesFor.id}`, { notes, status: "completed" });
      toast.success("تم حفظ الملاحظات");
      setNotesFor(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!await appConfirm("حذف الجلسة؟")) return;
    try { await api.delete(`/sessions/${id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardLayout>
      <div data-testid="mentor-sessions">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">الجلسات الإرشادية</div>
            <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">جلساتي مع الطلاب</h1>
            <p className="mt-2 text-slate-600">جدولة الجلسات عبر Microsoft Teams ومتابعة الملاحظات.</p>
          </div>
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 transition-colors" data-testid="new-session-btn">
            <Plus className="h-5 w-5" /> جلسة جديدة
          </button>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : error ? (
          <ErrorState message={error} onRetry={load} testid="mentor-sessions-error" />
        ) : sessions.length === 0 ? (
          <EmptyState
            icon={Video}
            title="لا توجد جلسات بعد"
            description="ابدأ بجدولة جلستك الأولى مع أحد الطلاب."
            testid="mentor-sessions-empty"
          />
        ) : (
          <div className="space-y-3">
            {sessions.map((s) => (
              <div id={`mentor-session-${s.id}`} className={`scroll-mt-6 rounded-2xl ${focusedSession === s.id ? "ring-2 ring-teal-400" : ""}`}><SessionCard key={s.id} s={s} onNotes={() => { setNotesFor(s); setNotes(s.notes || ""); }} onDelete={() => del(s.id)} onChange={load} isMentor /></div>
            ))}
          </div>
        )}

        {showForm && (
          <Modal onClose={() => setShowForm(false)} title="جدولة جلسة جديدة">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">الطالب</label>
                <select value={form.application_id} onChange={(e) => { const selected = students.find((item) => item.application_id === e.target.value); setForm({ ...form, application_id: e.target.value, student_id: selected?.id || "" }); }} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="ses-student">
                  <option value="">اختر طالباً</option>
                  {students.map((s) => <option key={s.application_id} value={s.application_id}>{s.name}{s.association_name ? ` — ${s.association_name}` : ""}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">نوع الجلسة</label>
                <select value={form.session_type} onChange={(e) => setForm({ ...form, session_type: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3" data-testid="ses-type">
                  <option value="individual">فردية — الموجّه والطالب</option>
                  <option value="tripartite">ثلاثية — الموجّه والطالب والجمعية</option>
                </select>
                {form.session_type === "tripartite" && form.student_id && <div className={"mt-2 rounded-xl border p-3 text-sm " + (selectedStudent?.association_id ? "border-teal-200 bg-teal-50 text-teal-900" : "border-rose-200 bg-rose-50 text-rose-800")} data-testid="ses-association">{selectedStudent?.association_id ? "ستُدعى تلقائيًا: " + selectedStudent.association_name : "لا توجد جمعية نشطة مرتبطة برحلة هذا الطالب"}</div>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">عنوان الجلسة</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="متابعة الأسبوع الأول" className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="ses-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">التاريخ والوقت</label>
                  <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="ses-date" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">المدة (دقيقة)</label>
                  <input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none arabic-numerals" />
                </div>
              </div>
              <label className="flex min-h-11 items-center gap-3 rounded-xl border border-slate-200 p-3"><input type="checkbox" checked={form.repeat_weekly} onChange={(e) => setForm({ ...form, repeat_weekly: e.target.checked })} data-testid="ses-repeat-weekly"/><span className="text-sm font-bold text-slate-700">تكرار الجلسة أسبوعيًا<small className="block font-normal text-slate-500">موعد ثابت في نفس اليوم والوقت</small></span></label>
              {form.repeat_weekly && <div><label className="block text-sm font-medium text-slate-700 mb-2">عدد الأسابيع (2–12)</label><input type="number" min="2" max="12" value={form.repeat_weeks} onChange={(e) => setForm({ ...form, repeat_weeks: Math.min(12, Math.max(2, Number(e.target.value) || 2)) })} className="w-full rounded-lg border border-slate-300 px-4 py-3" data-testid="ses-repeat-weeks"/></div>}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">الأجندة (اختياري)</label>
                <textarea rows={3} value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setShowForm(false)} className="flex-1 px-5 py-3 rounded-lg text-slate-700 font-medium hover:bg-slate-100">إلغاء</button>
              <button onClick={create} disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="save-ses-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                جدولة
              </button>
            </div>
          </Modal>
        )}

        {notesFor && (
          <Modal onClose={() => setNotesFor(null)} title="ملاحظات ما بعد الجلسة">
            <textarea rows={6} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="لخّص أهم النقاط، مستوى الطالب، والتوصيات..." className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="ses-notes" />
            <div className="mt-6 flex gap-3">
              <button onClick={() => setNotesFor(null)} className="flex-1 px-5 py-3 rounded-lg text-slate-700 font-medium hover:bg-slate-100">إلغاء</button>
              <button onClick={saveNotes} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600" data-testid="save-notes-btn">
                <Save className="h-4 w-4" /> حفظ وإنهاء
              </button>
            </div>
          </Modal>
        )}
      </div>
    </DashboardLayout>
  );
}

export function SessionCard({ s, onNotes, onDelete, onChange, isMentor }) {
  const scheduledDate = new Date(s.scheduled_at);
  const isPast = scheduledDate < new Date();
  const hasTeamsLink = s.meeting_provider === "microsoft_teams" && !!s.join_url;
  return (
    <div className="p-4 bg-white rounded-2xl border border-slate-200 card-shadow flex flex-col md:flex-row md:items-center gap-4" data-testid={`session-${s.id}`}>
      <div className="h-14 w-14 rounded-xl bg-sky-50 text-sky-700 grid place-items-center shrink-0">
        <Video className="h-6 w-6" strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-heading font-bold text-slate-900">{s.title}</div>
        <div className="text-sm text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {isMentor ? s.student_name : s.mentor_name}</span>
          {s.session_type === "tripartite" && <span className="inline-flex items-center gap-1 font-bold text-teal-700">جلسة ثلاثية · {s.association_name}{!isMentor && s.student_name ? " · " + s.student_name : ""}</span>}
          <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {scheduledDate.toLocaleString("ar-SA")}</span>
          <span className="arabic-numerals">{s.duration_minutes} دقيقة</span>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
          s.status === "completed" ? "bg-emerald-50 text-emerald-700"
            : s.status === "cancelled" ? "bg-red-50 text-red-700"
            : isPast ? "bg-amber-50 text-amber-700" : "bg-sky-50 text-sky-700"
        }`}>
          {s.status === "completed" ? "منتهية" : s.status === "cancelled" ? "ملغاة" : isPast ? "متأخرة" : "قادمة"}
        </span>
        {hasTeamsLink ? (
          <a href={s.join_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-700 text-white text-xs font-semibold hover:bg-teal-600 transition-colors" data-testid={`join-${s.id}`}>
            <ExternalLink className="h-3.5 w-3.5" /> فتح Teams
          </a>
        ) : (
          <button
            onClick={async () => {
              try {
                await api.post(`/sessions/${s.id}/regenerate-meeting`);
                toast.success("تم إنشاء رابط Teams");
                if (typeof onChange === "function") onChange();
              } catch (e) { toast.error(formatApiError(e)); }
            }}
            className="inline-flex min-h-11 items-center gap-1 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold hover:bg-amber-100"
            data-testid={`regen-${s.id}`}
            title={s.meeting_error || "إنشاء رابط Microsoft Teams"}
          >
            إعادة محاولة Teams
          </button>
        )}
        {isMentor && (
          <>
            <button onClick={onNotes} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-teal-700" title="ملاحظات"><FileText className="h-4 w-4" /></button>
            <button onClick={onDelete} className="p-2 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </>
        )}
      </div>
    </div>
  );
}

export function Modal({ children, onClose, title }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm overflow-y-auto p-4"
      onClick={onClose}
      data-testid="app-modal-backdrop"
    >
      <div className="min-h-full flex items-start sm:items-center justify-center py-6">
        <div
          className="w-full max-w-lg bg-white rounded-2xl p-5 shadow-2xl max-h-[calc(100vh-3rem)] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6 sticky top-0 bg-white pb-3 -mx-6 sm:-mx-8 px-6 sm:px-8 z-10">
            <h3 className="text-xl sm:text-2xl font-heading font-black text-slate-900">{title}</h3>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 min-h-[44px] min-w-[44px] inline-flex items-center justify-center" aria-label="إغلاق"><X className="h-5 w-5" /></button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
